type ColorIntent = 'text' | 'fill'

const PATCH_STYLE_ID = 'jiuda-univer-hex-color-input-patch-style-v23-native-command'
const MODE_TRIGGER_CLASS = 'jiuda-hex-only-color-mode'
const HIDDEN_MODE_LIST_CLASS = 'jiuda-hidden-color-mode-list'
const FONT_COLOR_COMMAND_ID = 'sheet.command.set-range-text-color'
const FILL_COLOR_COMMAND_ID = 'sheet.command.set-background-color'
const FONT_COLOR_ICON_CLASS = 'univerjs-icon-font-color'
const FILL_COLOR_ICON_CLASS = 'univerjs-icon-paint-bucket'

let observer: MutationObserver | null = null
let installed = false
let scheduled = false
let colorIntent: ColorIntent | null = null
let colorIntentUntil = 0
let rememberedRange: any | null = null

interface HexInputDraftState {
  draft: string
  intent: ColorIntent
}

const hexInputDraftStates = new WeakMap<HTMLInputElement, HexInputDraftState>()
const INTENT_TTL_MS = 60_000

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined'

const getGlobalUniverAPI = () => {
  if (!isBrowser()) return null
  return (window as any).__JIUDA_UNIVER_API__ || (window as any).univerAPI || null
}

export const setJiudaActiveUniverAPI = (api: any | null) => {
  if (!isBrowser()) return
  Object.assign(window as any, { __JIUDA_UNIVER_API__: api })
}

const normalizeHex = (value: string) => value
  .trim()
  .replace(/^#/, '')
  .replace(/[^0-9A-Fa-f]/g, '')
  .slice(0, 6)
  .toUpperCase()

const isFullHex = (value: string) => /^[0-9A-F]{6}$/.test(value)
const toHexColor = (value: string) => {
  const normalized = normalizeHex(value)
  return isFullHex(normalized) ? `#${normalized}` : ''
}

const getActiveRange = () => {
  const api = getGlobalUniverAPI()
  return api?.getActiveWorkbook?.()
    ?.getActiveSheet?.()
    ?.getSelection?.()
    ?.getActiveRange?.() || null
}

const rememberActiveRange = () => {
  const range = getActiveRange()
  if (range) rememberedRange = range
}

const executeCommand = (commandId: string, params: any) => {
  const api = getGlobalUniverAPI()
  if (!api) return false

  if (typeof api.syncExecuteCommand === 'function') {
    return api.syncExecuteCommand(commandId, params)
  }
  if (typeof api.executeCommand === 'function') {
    return api.executeCommand(commandId, params)
  }
  if (typeof api._commandService?.syncExecuteCommand === 'function') {
    return api._commandService.syncExecuteCommand(commandId, params)
  }
  if (typeof api._commandService?.executeCommand === 'function') {
    return api._commandService.executeCommand(commandId, params)
  }
  return false
}

/**
 * 必须走 Univer 自己的范围样式命令。
 * 直接 matrix.setValue / FRange.setFontColor 会绕过工具栏的 Observable、样式缓存和撤销栈，
 * 正是此前字体实际正确但下拉框长期停留在 Arial 的根因之一。
 */
const applyColorToSelection = (intent: ColorIntent, hexColor: string) => {
  if (!/^#[0-9A-F]{6}$/i.test(hexColor)) return false

  const commandId = intent === 'text' ? FONT_COLOR_COMMAND_ID : FILL_COLOR_COMMAND_ID
  const run = () => executeCommand(commandId, { value: hexColor })

  try {
    const result = run()
    if (result !== false) return true
  } catch (error) {
    console.warn('Univer 颜色命令首次执行失败，尝试恢复原选区后重试:', error)
  }

  // 极少数情况下颜色面板会让当前选区暂时丢失。只恢复选区后重试原生命令，
  // 不再回退到底层单元格直接写入。
  try {
    rememberedRange?.activate?.()
    window.setTimeout(run, 0)
    return true
  } catch (error) {
    console.warn('恢复选区后执行 Univer 颜色命令失败:', error)
    return false
  }
}

const isHexModeTrigger = (element: Element | null): element is HTMLElement => {
  if (!(element instanceof HTMLElement) || element.tagName.toLowerCase() !== 'a') return false
  const text = (element.textContent || '').trim().toUpperCase()
  const className = element.className.toString()
  return text === 'HEX' && className.includes('univer-w-32') && className.includes('univer-justify-between')
}

const isColorModeList = (element: Element): element is HTMLElement => {
  if (!(element instanceof HTMLElement)) return false
  const links = Array.from(element.querySelectorAll(':scope > a'))
  if (links.length < 3) return false
  const labels = links.map((link) => (link.textContent || '').trim().toUpperCase())
  return labels.includes('HEX') && labels.includes('HSL') && labels.includes('RGB')
}

const isHexColorInput = (input: HTMLInputElement) => {
  if (input.maxLength !== 6) return false
  const className = input.className.toString()
  const parentText = (input.parentElement?.textContent || '').trim()
  const previousText = (input.previousElementSibling?.textContent || '').trim()
  const nextText = (input.nextElementSibling?.textContent || '').trim()
  const isInUniverDom = !!input.closest('[class*="univer-"]')
  return isInUniverDom && (
    className.includes('univer-uppercase')
    || previousText === '#'
    || nextText === '#'
    || parentText.startsWith('#')
  )
}

const findToolbarColorIntent = (target: Element | null): ColorIntent | null => {
  if (!(target instanceof Element)) return null

  const commandElement = target.closest?.('[data-u-command]') as HTMLElement | null
  const commandId = commandElement?.dataset?.uCommand || commandElement?.getAttribute('data-u-command') || ''
  if (commandId === FONT_COLOR_COMMAND_ID) return 'text'
  if (commandId === FILL_COLOR_COMMAND_ID) return 'fill'

  let current: Element | null = target
  for (let depth = 0; current && current !== document.body && depth < 8; depth += 1) {
    if (current.querySelector?.(`.${FONT_COLOR_ICON_CLASS}`) || current.classList?.contains(FONT_COLOR_ICON_CLASS)) return 'text'
    if (current.querySelector?.(`.${FILL_COLOR_ICON_CLASS}`) || current.classList?.contains(FILL_COLOR_ICON_CLASS)) return 'fill'
    current = current.parentElement
  }
  return null
}

const setIntent = (intent: ColorIntent) => {
  colorIntent = intent
  colorIntentUntil = Date.now() + INTENT_TTL_MS
  rememberActiveRange()
}

const getActiveColorIntent = (): ColorIntent | null => {
  if (Date.now() > colorIntentUntil) colorIntent = null
  return colorIntent
}

const stopEvent = (event: Event) => {
  event.preventDefault()
  event.stopPropagation()
  event.stopImmediatePropagation()
}

const getNativeInputValueDescriptor = () => Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')

const setInputValue = (input: HTMLInputElement, value: string) => {
  const descriptor = getNativeInputValueDescriptor()
  descriptor?.set?.call(input, value)
  if (!descriptor?.set) input.value = value
  input.setAttribute('value', value)
}

const selectInput = (input: HTMLInputElement) => {
  window.setTimeout(() => {
    try {
      input.focus()
      input.setSelectionRange(0, input.value.length)
    } catch {
      // ignore
    }
  }, 0)
}

const getDraftState = (input: HTMLInputElement, intent: ColorIntent) => {
  let state = hexInputDraftStates.get(input)
  if (!state || state.intent !== intent) {
    state = { draft: normalizeHex(input.value), intent }
    hexInputDraftStates.set(input, state)
  }
  return state
}

const applyDraftIfComplete = (input: HTMLInputElement, state: HexInputDraftState) => {
  const hexColor = toHexColor(state.draft)
  if (!hexColor) return false
  const applied = applyColorToSelection(state.intent, hexColor)
  if (applied) selectInput(input)
  return applied
}

const onToolbarPointerDown = (event: Event) => {
  const intent = findToolbarColorIntent(event.target as Element | null)
  if (intent) setIntent(intent)
}

const onColorPickerBeforeInput = (event: InputEvent) => {
  const intent = getActiveColorIntent()
  const input = event.target
  if (!intent || !(input instanceof HTMLInputElement) || !isHexColorInput(input)) return

  const inserted = normalizeHex(event.data || '')
  if (event.inputType?.startsWith('insert') && event.data && !inserted) {
    stopEvent(event)
    return
  }

  // 满6位后继续输入时，直接把新字符作为下一轮HEX值开头。
  if (event.inputType?.startsWith('insert') && inserted && isFullHex(normalizeHex(input.value))) {
    const start = input.selectionStart ?? input.value.length
    const end = input.selectionEnd ?? start
    if (start === end && start >= input.value.length) {
      stopEvent(event)
      const state = getDraftState(input, intent)
      state.draft = inserted
      setInputValue(input, inserted)
      applyDraftIfComplete(input, state)
    }
  }
}

const onColorPickerInput = (event: Event) => {
  const intent = getActiveColorIntent()
  const input = event.target
  if (!intent || !(input instanceof HTMLInputElement) || !isHexColorInput(input)) return

  // 阻止Univer原ColorPicker把半截HEX自动改写，但最终颜色仍由Univer正式命令设置。
  event.stopPropagation()
  event.stopImmediatePropagation()

  const state = getDraftState(input, intent)
  state.draft = normalizeHex(input.value)
  if (input.value !== state.draft) setInputValue(input, state.draft)
  applyDraftIfComplete(input, state)
}

const onColorPickerKeyDown = (event: KeyboardEvent) => {
  const intent = getActiveColorIntent()
  const input = event.target
  if (!intent || !(input instanceof HTMLInputElement) || !isHexColorInput(input)) return

  if (event.key === 'Enter') {
    const state = getDraftState(input, intent)
    state.draft = normalizeHex(input.value || state.draft)
    if (isFullHex(state.draft)) {
      stopEvent(event)
      applyColorToSelection(intent, `#${state.draft}`)
      input.blur()
      colorIntent = null
    }
  } else if (event.key === 'Escape') {
    input.blur()
  }
}

const onColorPickerPaste = (event: ClipboardEvent) => {
  const intent = getActiveColorIntent()
  const input = event.target
  if (!intent || !(input instanceof HTMLInputElement) || !isHexColorInput(input)) return

  const pasted = normalizeHex(event.clipboardData?.getData('text') || '')
  if (!pasted) return
  stopEvent(event)
  const state = getDraftState(input, intent)
  state.draft = pasted
  setInputValue(input, pasted)
  applyDraftIfComplete(input, state)
}

const onColorPickerFocusIn = (event: FocusEvent) => {
  const intent = getActiveColorIntent()
  const input = event.target
  if (!intent || !(input instanceof HTMLInputElement) || !isHexColorInput(input)) return
  const state = getDraftState(input, intent)
  state.draft = normalizeHex(input.value || state.draft)
  if (isFullHex(state.draft)) selectInput(input)
}

const stopModeDropdown = (event: Event) => {
  const target = event.target as Element | null
  if (!target?.closest?.(`.${MODE_TRIGGER_CLASS}`)) return
  stopEvent(event)
}

const applyHexOnlyMode = () => {
  document.querySelectorAll<HTMLElement>('a').forEach((anchor) => {
    if (!isHexModeTrigger(anchor)) return
    anchor.classList.add(MODE_TRIGGER_CLASS)
    anchor.setAttribute('aria-disabled', 'true')
    anchor.setAttribute('title', '仅保留 HEX 显示')
    Array.from(anchor.children).forEach((child) => {
      if ((child.textContent || '').trim().toUpperCase() !== 'HEX') (child as HTMLElement).style.display = 'none'
    })
  })

  document.querySelectorAll<HTMLElement>('div').forEach((div) => {
    if (!isColorModeList(div)) return
    div.classList.add(HIDDEN_MODE_LIST_CLASS)
    div.querySelectorAll('a').forEach((link) => {
      if ((link.textContent || '').trim().toUpperCase() !== 'HEX') link.remove()
    })
  })
}

const runPatch = () => {
  scheduled = false
  applyHexOnlyMode()
}

const schedulePatch = () => {
  if (scheduled || !isBrowser()) return
  scheduled = true
  window.requestAnimationFrame(runPatch)
}

const injectStyle = () => {
  if (document.getElementById(PATCH_STYLE_ID)) return
  const style = document.createElement('style')
  style.id = PATCH_STYLE_ID
  style.textContent = `
.${MODE_TRIGGER_CLASS} { cursor: default !important; user-select: none !important; }
.${MODE_TRIGGER_CLASS} svg,
.${MODE_TRIGGER_CLASS} .univer-size-5,
.${MODE_TRIGGER_CLASS} [class*="dropdown"] { display: none !important; }
.${HIDDEN_MODE_LIST_CLASS} { display: none !important; pointer-events: none !important; }
`
  document.head.appendChild(style)
}

export const installUniverHexColorInputPatch = () => {
  if (!isBrowser() || installed) return
  installed = true
  injectStyle()

  document.addEventListener('pointerdown', onToolbarPointerDown, true)
  document.addEventListener('mousedown', stopModeDropdown, true)
  document.addEventListener('click', stopModeDropdown, true)
  document.addEventListener('beforeinput', onColorPickerBeforeInput, true)
  document.addEventListener('input', onColorPickerInput, true)
  document.addEventListener('keydown', onColorPickerKeyDown, true)
  document.addEventListener('paste', onColorPickerPaste, true)
  document.addEventListener('focusin', onColorPickerFocusIn, true)

  observer = new MutationObserver(schedulePatch)
  observer.observe(document.body, { childList: true, subtree: true })
  schedulePatch()
}

export const uninstallUniverHexColorInputPatch = () => {
  if (!isBrowser() || !installed) return
  installed = false
  observer?.disconnect()
  observer = null

  document.removeEventListener('pointerdown', onToolbarPointerDown, true)
  document.removeEventListener('mousedown', stopModeDropdown, true)
  document.removeEventListener('click', stopModeDropdown, true)
  document.removeEventListener('beforeinput', onColorPickerBeforeInput, true)
  document.removeEventListener('input', onColorPickerInput, true)
  document.removeEventListener('keydown', onColorPickerKeyDown, true)
  document.removeEventListener('paste', onColorPickerPaste, true)
  document.removeEventListener('focusin', onColorPickerFocusIn, true)
  document.getElementById(PATCH_STYLE_ID)?.remove()
}
